const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10])
const ZLIB_HEADER = new Uint8Array([0x78, 0x01])

const crcTable = new Uint32Array(256)
for (let i = 0; i < crcTable.length; i++) {
  let c = i
  for (let k = 0; k < 8; k++) {
    c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
  }
  crcTable[i] = c >>> 0
}

function writeUint32(buffer: Uint8Array, offset: number, value: number): void {
  buffer[offset] = (value >>> 24) & 0xff
  buffer[offset + 1] = (value >>> 16) & 0xff
  buffer[offset + 2] = (value >>> 8) & 0xff
  buffer[offset + 3] = value & 0xff
}

function readUint32(buffer: Uint8Array, offset: number): number {
  return (
    ((buffer[offset] << 24) |
      (buffer[offset + 1] << 16) |
      (buffer[offset + 2] << 8) |
      buffer[offset + 3]) >>>
    0
  )
}

function chunkTypeBytes(type: string): Uint8Array {
  return new TextEncoder().encode(type)
}

function crc32(type: Uint8Array, data: Uint8Array): number {
  let crc = 0xffffffff
  for (const byte of type) {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8)
  }
  for (const byte of data) {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

function adler32(data: Uint8Array): number {
  let a = 1
  let b = 0
  for (const byte of data) {
    a = (a + byte) % 65521
    b = (b + a) % 65521
  }
  return ((b << 16) | a) >>> 0
}

function createChunk(type: string, data: Uint8Array): Uint8Array {
  const typeBytes = chunkTypeBytes(type)
  const chunk = new Uint8Array(12 + data.length)
  writeUint32(chunk, 0, data.length)
  chunk.set(typeBytes, 4)
  chunk.set(data, 8)
  writeUint32(chunk, 8 + data.length, crc32(typeBytes, data))
  return chunk
}

function createZlibStore(data: Uint8Array): Uint8Array {
  const blocks: Uint8Array[] = []
  let offset = 0

  while (offset < data.length) {
    const length = Math.min(0xffff, data.length - offset)
    const isFinalBlock = offset + length >= data.length
    const block = new Uint8Array(5 + length)
    block[0] = isFinalBlock ? 1 : 0
    block[1] = length & 0xff
    block[2] = (length >>> 8) & 0xff
    block[3] = ~length & 0xff
    block[4] = (~length >>> 8) & 0xff
    block.set(data.subarray(offset, offset + length), 5)
    blocks.push(block)
    offset += length
  }

  const totalLength =
    ZLIB_HEADER.length +
    blocks.reduce((sum, block) => sum + block.length, 0) +
    4
  const output = new Uint8Array(totalLength)
  output.set(ZLIB_HEADER, 0)

  let writeOffset = ZLIB_HEADER.length
  for (const block of blocks) {
    output.set(block, writeOffset)
    writeOffset += block.length
  }

  writeUint32(output, writeOffset, adler32(data))
  return output
}

function inflateZlibStore(data: Uint8Array): Uint8Array {
  if (data.length < 6) throw new Error('Invalid zlib stream')

  let offset = 2
  const blocks: Uint8Array[] = []

  while (offset < data.length - 4) {
    const header = data[offset++]
    const blockType = (header >>> 1) & 0x3
    if (blockType !== 0) {
      throw new Error('Unsupported PNG compression block')
    }

    const length = data[offset] | (data[offset + 1] << 8)
    const inverseLength = data[offset + 2] | (data[offset + 3] << 8)
    offset += 4

    if ((length ^ inverseLength) !== 0xffff) {
      throw new Error('Invalid zlib store block length')
    }

    blocks.push(data.subarray(offset, offset + length))
    offset += length

    if (header & 1) break
  }

  const output = new Uint8Array(
    blocks.reduce((sum, block) => sum + block.length, 0)
  )
  let writeOffset = 0
  for (const block of blocks) {
    output.set(block, writeOffset)
    writeOffset += block.length
  }
  return output
}

export function encodeRgbaPng(imageData: ImageData): Blob {
  const { width, height, data } = imageData
  const scanlineLength = width * 4 + 1
  const scanlines = new Uint8Array(scanlineLength * height)

  for (let y = 0; y < height; y++) {
    const scanlineOffset = y * scanlineLength
    const dataOffset = y * width * 4
    scanlines[scanlineOffset] = 0
    scanlines.set(
      data.subarray(dataOffset, dataOffset + width * 4),
      scanlineOffset + 1
    )
  }

  const ihdr = new Uint8Array(13)
  writeUint32(ihdr, 0, width)
  writeUint32(ihdr, 4, height)
  ihdr[8] = 8
  ihdr[9] = 6

  const chunks = [
    PNG_SIGNATURE,
    createChunk('IHDR', ihdr),
    createChunk('IDAT', createZlibStore(scanlines)),
    createChunk('IEND', new Uint8Array())
  ]
  const png = new Uint8Array(
    chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  )
  let offset = 0
  for (const chunk of chunks) {
    png.set(chunk, offset)
    offset += chunk.length
  }

  return new Blob([png.buffer], { type: 'image/png' })
}

export function decodeStoredRgbaPng(buffer: ArrayBuffer): ImageData {
  const bytes = new Uint8Array(buffer)
  if (PNG_SIGNATURE.some((byte, index) => bytes[index] !== byte)) {
    throw new Error('Invalid PNG signature')
  }

  let offset = PNG_SIGNATURE.length
  let width = 0
  let height = 0
  const idatChunks: Uint8Array[] = []

  while (offset < bytes.length) {
    const length = readUint32(bytes, offset)
    const type = new TextDecoder().decode(
      bytes.subarray(offset + 4, offset + 8)
    )
    const data = bytes.subarray(offset + 8, offset + 8 + length)
    offset += 12 + length

    if (type === 'IHDR') {
      width = readUint32(data, 0)
      height = readUint32(data, 4)
      if (data[8] !== 8 || data[9] !== 6) {
        throw new Error('Unsupported PNG format')
      }
    } else if (type === 'IDAT') {
      idatChunks.push(data)
    } else if (type === 'IEND') {
      break
    }
  }

  const idat = new Uint8Array(
    idatChunks.reduce((sum, chunk) => sum + chunk.length, 0)
  )
  let idatOffset = 0
  for (const chunk of idatChunks) {
    idat.set(chunk, idatOffset)
    idatOffset += chunk.length
  }

  const scanlines = inflateZlibStore(idat)
  const data = new Uint8ClampedArray(width * height * 4)
  const scanlineLength = width * 4 + 1

  for (let y = 0; y < height; y++) {
    const scanlineOffset = y * scanlineLength
    if (scanlines[scanlineOffset] !== 0) {
      throw new Error('Unsupported PNG filter')
    }
    data.set(
      scanlines.subarray(scanlineOffset + 1, scanlineOffset + scanlineLength),
      y * width * 4
    )
  }

  return new ImageData(data, width, height)
}
